import React, { useState, useCallback, useRef } from 'react';
import {
  Button, Box, List, ListItem, ListItemText, ListItemIcon, LinearProgress,
  Typography, Paper, TextField, Snackbar, Alert, Tabs, Tab, CircularProgress
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/material/styles';
import { backendUrl } from '../config';
import axios from 'axios';

// Configuration de base pour Ollama (même que dans iaApp.js)
const OLLAMA_API_URL = process.env.REACT_APP_OLLAMA_API_URL || 'https://www.4styl.com/api/embeddings';
const OLLAMA_MODEL = process.env.REACT_APP_OLLAMA_MODEL || 'nomic-embed-text';

// Utilitaire pour passer par le backend (même que dans iaApp.js)
async function proxyIaApiRequest({ target_url, payload, headers = {} }) {
  const res = await fetch(`${backendUrl}/api/proxy-ia`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ target_url, payload, headers })
  });
  if (!res.ok) throw new Error('Erreur proxy IA: ' + res.status);
  return res;
}

// Composant de glisser-déposer personnalisé
const DropZone = styled('div')(({ theme, isDragActive }) => ({
  border: `2px dashed ${isDragActive ? theme.palette.primary.main : '#ccc'}`,
  borderRadius: '4px',
  padding: '40px',
  textAlign: 'center',
  margin: '20px 0',
  backgroundColor: isDragActive ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
  cursor: 'pointer',
  transition: 'all 0.3s ease'
}));

const VectorApp = () => {
  // États pour la vectorisation
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [chunkSize, setChunkSize] = useState(1000);
  const [chunkOverlap, setChunkOverlap] = useState(200);
  
  // États pour la recherche sémantique
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const fileInputRef = useRef(null);
  
  // Cache pour stocker les embeddings déjà calculés
  const embeddingCache = useRef(new Map());

  // Gestion du glisser-déposer
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)]);
    }
  };

  // Sélection de fichiers via le bouton
  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFiles(prev => [...prev, ...Array.from(e.target.files)]);
    }
  };

  // Découpage du texte en chunks
  const chunkText = (text, size, overlap) => {
    // Validation des entrées
    if (typeof text !== 'string' || text.length === 0) {
      console.error('Erreur: Le texte fourni est vide ou invalide');
      return [];
    }
    
    size = Math.max(1, Math.floor(Number(size)) || 1000);
    overlap = Math.max(0, Math.min(overlap, size - 1)); // L'overlap doit être inférieur à la taille du chunk
    
    const chunks = [];
    let start = 0;
    let iteration = 0;
    const maxIterations = 1000; // Sécurité contre les boucles infinies
    
    while (start < text.length && iteration < maxIterations) {
      const end = Math.min(start + size, text.length);
      const chunk = text.substring(start, end);
      
      if (chunk.length === 0) {
        console.warn('Chunk vide détecté, arrêt du traitement');
        break;
      }
      
      chunks.push(chunk);
      start = end - overlap;
      
      // Éviter les boucles infinies ou les régressions
      if (start <= 0 || start >= text.length) break;
      
      // S'assurer que nous progressons
      const prevEnd = end;
      if (start >= prevEnd) {
        console.warn('Aucune progression détectée, arrêt du traitement');
        break;
      }
      
      iteration++;
    }
    
    console.log(`Découpage terminé: ${chunks.length} chunks créés`);
    return chunks;
  };

  // Génération d'embeddings avec Ollama - version avec cache
  const generateEmbedding = useCallback(async (text) => {
    try {
      // Vérifier que le texte n'est pas vide
      if (!text || typeof text !== 'string' || text.trim() === '') {
        console.warn('Texte vide fourni pour la génération d\'embedding');
        return new Array(768).fill(0);
      }

      // Vérifier d'abord dans le cache
      const cacheKey = text.trim();
      if (embeddingCache.current.has(cacheKey)) {
        console.log('Embedding récupéré depuis le cache pour le texte:', 
          cacheKey.length > 50 ? cacheKey.substring(0, 50) + '...' : cacheKey);
        return embeddingCache.current.get(cacheKey);
      }

      // Préparer les données exactement comme dans la requête cURL qui fonctionne
      const requestData = {
        model: OLLAMA_MODEL,
        prompt: text
      };

      // Utiliser fetch au lieu d'axios pour correspondre exactement au format cURL
      const response = await fetch(OLLAMA_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erreur HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      
      // Vérifier la structure de la réponse
      if (!data || !data.embedding) {
        console.error('Format de réponse inattendu:', data);
        throw new Error('Format de réponse inattendu de l\'API d\'embeddings');
      }
      
      console.log('Nouvel embedding généré, dimensions:', data.embedding.length);
      
      // Mettre en cache le résultat
      embeddingCache.current.set(cacheKey, data.embedding);
      return data.embedding;
      
    } catch (error) {
      console.error('Erreur lors de la génération des embeddings:', {
        stack: error.stack,
        text: text ? text.substring(0, 100) + (text.length > 100 ? '...' : '') : 'Aucun texte fourni'
      });
      
      // En cas d'échec, retourner un vecteur nul plutôt que de faire échouer tout le processus
      return new Array(768).fill(0);
    }
  }, []); // Aucune dépendance externe nécessaire

  // Sauvegarde dans LanceDB via l'API
  const saveToVectorDB = async (chunksWithEmbeddings, filename) => {
    try {
      console.log(`Préparation de l'envoi de ${chunksWithEmbeddings.length} chunks pour ${filename}`);
      
      // Préparer les données pour l'envoi
      // Concaténer tout le contenu des chunks pour le champ content
      const fileContent = chunksWithEmbeddings.map(chunk => chunk.text).join('\n\n');
      
      const payload = {
        files: [{
          name: filename,
          path: filename,
          content: fileContent, // Ajout du contenu complet du fichier
          chunks: chunksWithEmbeddings.map((chunk, index) => ({
            id: `${filename}_${index}`,
            text: chunk.text,
            metadata: {
              ...chunk.metadata,
              source: 'webdesk-vector-app',
            },
            embedding: chunk.embedding
          }))
        }]
      };

      console.log('Envoi des données au serveur:', JSON.stringify({
        fileCount: payload.files.length,
        firstFile: {
          name: payload.files[0]?.name,
          chunkCount: payload.files[0]?.chunks?.length,
          firstChunk: {
            id: payload.files[0]?.chunks?.[0]?.id,
            textLength: payload.files[0]?.chunks?.[0]?.text?.length,
            embeddingLength: payload.files[0]?.chunks?.[0]?.embedding?.length,
            metadata: payload.files[0]?.chunks?.[0]?.metadata
          }
        }
      }, null, 2));

      // Envoyer au backend
      const response = await axios.post(`${backendUrl}/api/vectorize`, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
        validateStatus: status => status < 500 // Ne pas lever d'erreur pour les 4xx
      });
      
      if (response.status === 422) {
        console.error('Erreur de validation:', response.data);
        throw new Error(`Erreur de validation: ${JSON.stringify(response.data.detail || response.data)}`);
      }
      
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde dans la base vectorielle:', error);
      throw error;
    }
  };

  // Traitement des fichiers
  const processFiles = async () => {
    if (files.length === 0) return;
    
    setIsProcessing(true);
    setProgress(0);
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // 1. Lire le fichier
        const text = await readFileAsText(file);
        
        // 2. Découper en chunks
        const chunks = chunkText(text, chunkSize, chunkOverlap);
        
        // 3. Générer les embeddings pour chaque chunk
        const chunksWithEmbeddings = [];
        for (let j = 0; j < chunks.length; j++) {
          const embedding = await generateEmbedding(chunks[j]);
          chunksWithEmbeddings.push({
            text: chunks[j],
            embedding,
            metadata: {
              chunkIndex: j,
              totalChunks: chunks.length,
              filename: file.name,
              fileType: file.type,
              fileSize: file.size,
              processedAt: new Date().toISOString()
            }
          });
          
          // Mettre à jour la progression
          const chunkProgress = ((j + 1) / chunks.length) * 100;
          const fileProgress = (i + (j + 1) / chunks.length) / files.length * 100;
          setProgress(fileProgress);
        }
        
        // 4. Sauvegarder dans LanceDB
        await saveToVectorDB(chunksWithEmbeddings, file.name);
        
        setProgress(((i + 1) / files.length) * 100);
      }
      
      setSnackbar({
        open: true,
        message: `Traitement de ${files.length} fichier(s) terminé avec succès !`,
        severity: 'success'
      });
      setFiles([]);
    } catch (error) {
      console.error('Erreur lors du traitement:', error);
      setSnackbar({
        open: true,
        message: `Erreur: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Fonction utilitaire pour lire un fichier comme texte
  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  // Supprimer un fichier de la liste
  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Fonction pour effectuer une recherche sémantique
  const handleSemanticSearch = async () => {
    if (!searchQuery.trim()) {
      setSnackbar({
        open: true,
        message: 'Veuillez entrer une requête de recherche',
        severity: 'warning'
      });
      return;
    }

    setIsSearching(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${backendUrl}/api/semantic-search`,
        { query: searchQuery, top_k: 5 },
        {
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          validateStatus: status => status < 500
        }
      );

      if (response.status === 200) {
        // La réponse est déjà au bon format : un tableau d'objets {document, score}
        setSearchResults(response.data || []);
      } else {
        throw new Error(response.data?.detail || 'Erreur lors de la recherche');
      }
    } catch (error) {
      console.error('Erreur recherche sémantique:', error);
      setSnackbar({
        open: true,
        message: `Erreur: ${error.message || 'Une erreur est survenue lors de la recherche'}`,
        severity: 'error'
      });
    } finally {
      setIsSearching(false);
    }
  };

return (
    <Box sx={{ p: 3, maxWidth: '1000px', margin: '0 auto' }}>
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Vectorisation de documents" />
        <Tab label="Recherche sémantique" />
      </Tabs>
      {activeTab === 0 ? (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Vectorisation de documents
          </Typography>
          <Typography variant="body1" paragraph>
            Glissez-déposez des fichiers (PDF, TXT, DOCX) ou cliquez pour sélectionner des fichiers.
            Les documents seront découpés en chunks et vectorisés pour la recherche sémantique.
          </Typography>
          
          <Box sx={{ mt: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Paramètres de découpage
            </Typography>
            <Box display="flex" gap={2} mb={3}>
              <TextField
                label="Taille des chunks (caractères)"
                type="number"
                value={chunkSize}
                onChange={(e) => setChunkSize(parseInt(e.target.value) || 1000)}
                disabled={isProcessing}
                fullWidth
                size="small"
              />
              <TextField
                label="Recouvrement (caractères)"
                type="number"
                value={chunkOverlap}
                onChange={(e) => setChunkOverlap(parseInt(e.target.value) || 200)}
                disabled={isProcessing}
                fullWidth
                size="small"
              />
            </Box>
            
            <Typography variant="h6" gutterBottom>
              Fichiers à vectoriser
            </Typography>
            
            <input
              type="file"
              id="file-upload"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
              multiple
              disabled={isProcessing}
              ref={fileInputRef}
            />
            
            <DropZone
              isDragActive={isDragging}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <CloudUploadIcon fontSize="large" color={isDragging ? "primary" : "action"} />
              <Typography variant="body1" sx={{ mt: 1 }}>
                {isDragging ? 'Déposez les fichiers ici' : 'Glissez-déposez des fichiers ou cliquez pour sélectionner'}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Formats supportés: .txt, .md, .pdf, .docx
              </Typography>
            </DropZone>
            
            {files.length > 0 && (
              <List dense sx={{ mt: 2, maxHeight: '300px', overflow: 'auto' }}>
                {files.map((file, index) => (
                  <ListItem 
                    key={index}
                    secondaryAction={
                      <Button 
                        color="error" 
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                        disabled={isProcessing}
                        startIcon={<DeleteIcon />}
                      >
                        Supprimer
                      </Button>
                    }
                  >
                    <ListItemIcon>
                      <InsertDriveFileIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary={file.name} 
                      secondary={`${(file.size / 1024).toFixed(1)} KB`} 
                    />
                  </ListItem>
                ))}
              </List>
            )}
            
            <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
              <Button 
                variant="outlined" 
                onClick={() => setFiles([])}
                disabled={files.length === 0 || isProcessing}
                startIcon={<DeleteIcon />}
              >
                Tout effacer
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={processFiles}
                disabled={files.length === 0 || isProcessing}
                startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {isProcessing ? `Traitement... ${Math.round(progress)}%` : 'Vectoriser'}
              </Button>
            </Box>
            
            {isProcessing && (
              <Box sx={{ width: '100%', mt: 2 }}>
                <LinearProgress variant="determinate" value={progress} />
                <Typography variant="caption" display="block" textAlign="center" mt={1}>
                  {Math.round(progress)}% complété
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      ) : (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Recherche Sémantique
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Votre question ou recherche"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSemanticSearch()}
              disabled={isSearching}
            />
            <Button
              variant="contained"
              onClick={handleSemanticSearch}
              disabled={isSearching || !searchQuery.trim()}
              sx={{ minWidth: '120px' }}
            >
              {isSearching ? <CircularProgress size={24} /> : 'Rechercher'}
            </Button>
          </Box>
          
          {isSearching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : searchResults.length > 0 ? (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                {searchResults.length} résultat{searchResults.length > 1 ? 's' : ''} trouvé{searchResults.length > 1 ? 's' : ''}
              </Typography>
              {searchResults.map((result, index) => (
                <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    {result.document.metadata?.source || 'Document sans titre'}
                    {result.document.metadata?.page && ` - Page ${result.document.metadata.page}`}
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {result.document.text}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Score: {result.score?.toFixed(3)}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : searchQuery ? (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
              Aucun résultat trouvé pour votre recherche.
            </Typography>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
              Entrez une requête pour effectuer une recherche sémantique dans vos documents vectorisés.
            </Typography>
          )}
        </Paper>
      )}
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VectorApp;
